/**
 * Simple Rebate Agreement Upload Modal - V2 Simplified
 *
 * Just file upload - backend extracts everything automatically:
 * - Supplier name (matches to existing supplier)
 * - Start/end dates
 * - All rebate rules and structure
 */

import React, { useState } from 'react';
import { TrashIcon, CircleNotchIcon } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Label } from 'src/components/ui/label';
import { Card, CardContent } from 'src/components/ui/card';
import PrimaryButton from 'src/components/common/PrimaryButton';
import CancelButton from 'src/components/common/CancelButton';
import FileUploadDropzone from 'src/components/documents/FileUploadDropzone';
import { UploadProgress } from 'src/types/UserDocuments';
import useCreateRebateAgreementV2 from 'src/api/rebates/useCreateRebateAgreementV2';
import FileIcon from 'src/components/documents/FileIcon';

const MAX_FILES = 3;

interface RebateAgreementUploadModalSimpleProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (agreementId?: string) => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export const RebateAgreementUploadModalSimple: React.FC<
  RebateAgreementUploadModalSimpleProps
> = ({ open, onClose, onSuccess }) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [error, setError] = useState<string>('');

  const createRebateAgreement = useCreateRebateAgreementV2();
  const isLoading = createRebateAgreement.isLoading;

  const handleFilesSelected = (files: File[]) => {
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
    ];

    const invalidFiles = files.filter(
      (file) => !allowedTypes.includes(file.type),
    );
    if (invalidFiles.length > 0) {
      setError('Please select only PDF, JPG, JPEG, or PNG files.');
      return;
    }

    if (files.length > MAX_FILES) {
      setError(`Please select up to ${MAX_FILES} files maximum.`);
      return;
    }

    setError('');
    setSelectedFiles(files);
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setError('');
  };

  const handleSubmit = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file to upload.');
      return;
    }

    try {
      const result = await createRebateAgreement.mutateAsync({
        files: selectedFiles,
        // V2: No supplier, dates, or name needed - backend extracts everything
      });

      console.log('Upload successful:', result);

      // Success - close modal and notify parent
      if (onSuccess) {
        onSuccess(result?.data?.id);
      }
      handleClose();
    } catch (err) {
      console.error('Upload error:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to upload agreement. Please try again.',
      );
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setSelectedFiles([]);
      setUploadProgress([]);
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Rebate Agreement</DialogTitle>
          <p className="text-sm text-gray-600 mt-2">
            {`We'll automatically extract the supplier, dates, and all rebate rules.`}
          </p>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-900">
              Upload up to {MAX_FILES} files (PDF, JPG, JPEG, or PNG)
            </p>
            <FileUploadDropzone
              onFilesSelected={handleFilesSelected}
              uploadProgress={uploadProgress}
              isUploading={isLoading}
              maxFiles={MAX_FILES}
              disabled={isLoading}
            />
          </div>

          {selectedFiles.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Selected File{selectedFiles.length > 1 ? 's' : ''}
              </Label>
              {selectedFiles.map((file, index) => (
                <Card key={index}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileIcon
                          fileName={file.name}
                          size="small"
                          showBackground={false}
                        />
                        <div>
                          <p className="text-sm font-medium">{file.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFile(index)}
                        disabled={isLoading}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <CancelButton onClick={handleClose}>Cancel</CancelButton>
          <PrimaryButton
            onClick={handleSubmit}
            disabled={isLoading || selectedFiles.length === 0}
          >
            {isLoading ? (
              <>
                <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              'Upload'
            )}
          </PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RebateAgreementUploadModalSimple;
